import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpSelect, ErpTable, toOptions } from "../../components/erp";
import { errorText } from "../../api/client";
import { AbReportEntry, abReportApi, useAbReportDropdowns } from "./api";

const SAMPLE_TYPES = ["Harvest", "Pre Harvest"];
const PARAMETERS = ["NA", "AOZ", "AMOZ", "AHD", "SEM", "CAP"];
const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = { LotId: "", SampleType: "Harvest", Parameter: "NA", Result: "", AOZ: "", SEM: "", CAP: "", AHD: "", AMOZ: "", Sulphite: "", Remarks: "", Shift: "", TestedBy: "", AbStatus: "" };

/** Port of v3/QualityControl/RmAbReport.php — per-date AB entry create + existing entries for that date.
 * Write semantics use live-data-consistent conventions (see backend app/modules/qc/ab_report.py docstring
 * and WORKFLOW_REGISTRY.md QcRmAbReport_write_semantics — AMBIGUOUS_KT). */
export default function AbReportDetail() {
  const [params] = useSearchParams();
  const analysisDate = params.get("AnalysisDate") || today();
  const month = params.get("Month") || String(new Date(analysisDate).getMonth() + 1);
  const year = params.get("Year") || String(new Date(analysisDate).getFullYear());
  const qc = useQueryClient();

  const dd = useAbReportDropdowns();
  const lots = useQuery({ queryKey: ["qc", "unreported-lots", month, year], queryFn: () => abReportApi.unreportedLots({ Month: month, Year: year }) });
  const entries = useQuery({ queryKey: ["qc", "ab-entries", analysisDate], queryFn: () => abReportApi.byDate(analysisDate) });

  const [reportDate, setReportDate] = useState(today());
  const [form, setForm] = useState(EMPTY);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const shifts = useMemo(() => (dd.data?.Data?.Options || []).filter((o) => o.Category === "Shift"), [dd.data]);
  const testStatuses = useMemo(() => (dd.data?.Data?.Options || []).filter((o) => o.Category === "TestStatus"), [dd.data]);
  const technologists = dd.data?.Data?.LabTechnologists || [];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setMsg(null);
    try {
      const r = await abReportApi.post({ ...form, AnalysisDate: analysisDate, ReportDate: reportDate });
      if (r.Error) setMsg({ ok: false, text: r.Error });
      else { setMsg({ ok: true, text: "AB report saved." }); setForm(EMPTY); qc.invalidateQueries({ queryKey: ["qc", "ab-entries", analysisDate] }); qc.invalidateQueries({ queryKey: ["qc", "unreported-lots", month, year] }); }
    } catch (ex) { setMsg({ ok: false, text: errorText(ex) }); } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="RM AB Report" location="Quality Control" />
      <div className="card mb-3"><div className="card-body">
        <form onSubmit={submit} data-testid="ab-report-form">
          <div className="row">
            <div className="form-group col-md-3 mb-3"><label className="form-label">Lot</label>
              <ErpSelect id="ab-report-lot" value={form.LotId} onChange={(v) => setForm((s) => ({ ...s, LotId: v }))}
                options={toOptions(lots.data?.Data, (l) => l.RmLotID, (l) => l.LotNumber)} placeholder="Select Lot" testId="ab-report-lot" />
            </div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Analysis Date</label>
              <input className="form-control" type="date" value={analysisDate} readOnly data-testid="ab-report-analysis-date" /></div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Report Date</label>
              <input className="form-control" type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} data-testid="ab-report-report-date" /></div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Sample Type</label>
              <select className="form-select" value={form.SampleType} onChange={(e) => setForm((s) => ({ ...s, SampleType: e.target.value }))} data-testid="ab-report-sample-type">
                {SAMPLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Parameter</label>
              <select className="form-select" value={form.Parameter} onChange={(e) => setForm((s) => ({ ...s, Parameter: e.target.value }))} data-testid="ab-report-parameter">
                {PARAMETERS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Result</label>
              <input className="form-control" value={form.Result} onChange={(e) => setForm((s) => ({ ...s, Result: e.target.value }))} data-testid="ab-report-result" /></div>
            {(["AOZ", "SEM", "CAP", "AHD", "AMOZ"] as const).map((k) => (
              <div className="form-group col-md-2 mb-3" key={k}><label className="form-label">{k}</label>
                <input className="form-control" value={form[k]} onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))} data-testid={`ab-report-${k.toLowerCase()}`} /></div>
            ))}
            <div className="form-group col-md-3 mb-3"><label className="form-label">Sulphite</label>
              <input className="form-control" value={form.Sulphite} onChange={(e) => setForm((s) => ({ ...s, Sulphite: e.target.value }))} data-testid="ab-report-sulphite" /></div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Shift</label>
              <select className="form-select" value={form.Shift} onChange={(e) => setForm((s) => ({ ...s, Shift: e.target.value }))} data-testid="ab-report-shift">
                <option value="">Select Shift</option>
                {shifts.map((o) => <option key={o.OptID} value={o.Value}>{o.Name}</option>)}
              </select>
            </div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Tested By</label>
              <select className="form-select" value={form.TestedBy} onChange={(e) => setForm((s) => ({ ...s, TestedBy: e.target.value }))} data-testid="ab-report-tested-by">
                <option value="">Select</option>
                {technologists.map((t) => <option key={t.LUID} value={t.LUID}>{t.LbuName}</option>)}
              </select>
            </div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Loading Status</label>
              <select className="form-select" value={form.AbStatus} onChange={(e) => setForm((s) => ({ ...s, AbStatus: e.target.value }))} data-testid="ab-report-status">
                <option value="">Select Status</option>
                {testStatuses.map((o) => <option key={o.OptID} value={o.Value}>{o.Name}</option>)}
              </select>
            </div>
            <div className="form-group col-md-6 mb-3"><label className="form-label">Remarks</label>
              <input className="form-control" value={form.Remarks} onChange={(e) => setForm((s) => ({ ...s, Remarks: e.target.value }))} data-testid="ab-report-remarks" /></div>
          </div>
          {msg && <Alert kind={msg.ok ? "success" : "danger"} testId="ab-report-msg">{msg.text}</Alert>}
          <button className="btn btn-primary" type="submit" disabled={saving} data-testid="ab-report-submit">{saving ? "Saving..." : "Submit"}</button>
        </form>
      </div></div>

      <div className="card"><div className="card-body">
        <h6 className="mb-3">Entries for {analysisDate}</h6>
        <ErpTable<AbReportEntry> testId="ab-report-entries-table" loading={entries.isLoading}
          error={entries.isError ? errorText(entries.error) : null} rows={entries.data?.Data || []}
          columns={[{ key: "LotNumber", title: "Lot" }, { key: "Parameter", title: "Parameter" }, { key: "Result", title: "Result" },
            { key: "Shift", title: "Shift" }, { key: "Remarks", title: "Remarks" }]} />
      </div></div>
    </>
  );
}
